import { apiFetch } from "../api/client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";

const API =
  process.env.REACT_APP_API_URL ||
  "https://api.wirelesspress.org";

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatDate(value) {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

function formatLabel(value) {
  if (!value) {
    return "Unknown";
  }

  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function ThreatIndicatorsPage() {
  const [items, setItems] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [selectedIndicator, setSelectedIndicator] =
    useState(null);

  const [relationshipGraph, setRelationshipGraph] =
    useState(null);

  const [graphLoading, setGraphLoading] =
    useState(false);

  const [indicatorTimeline, setIndicatorTimeline] =
    useState(null);

  const [timelineLoading, setTimelineLoading] =
    useState(false);

  const [indicatorHunts, setIndicatorHunts] =
    useState([]);

  const [huntsLoading, setHuntsLoading] =
    useState(false);

  const [huntLaunching, setHuntLaunching] =
    useState(false);

  const [huntMessage, setHuntMessage] =
    useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 25,
    total: 0,
    total_pages: 0,
    has_previous: false,
    has_next: false,
  });

  const [filters, setFilters] = useState({
    search: "",
    indicator_type: "",
    severity: "",
    feed_id: "",
    threat_category: "",
    threat_actor: "",
    campaign: "",
    malware_family: "",
    tlp: "",
    active: "",
    expired: "",
    minimum_confidence: 0,
    maximum_confidence: 100,
    minimum_reputation: -1000,
    maximum_reputation: 1000,
    sort: "updated_at",
    order: "desc",
  });

  const [draftSearch, setDraftSearch] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [detailsLoading, setDetailsLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const queryString = useMemo(() => {
    const parameters = new URLSearchParams();

    Object.entries(filters).forEach(
      ([key, value]) => {
        if (
          value !== "" &&
          value !== null &&
          value !== undefined
        ) {
          parameters.set(key, String(value));
        }
      }
    );

    parameters.set(
      "page",
      String(pagination.page)
    );

    parameters.set(
      "page_size",
      String(pagination.page_size)
    );

    return parameters.toString();
  }, [
    filters,
    pagination.page,
    pagination.page_size,
  ]);

  const loadIndicators = useCallback(
    async (silent = false) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const response = await apiFetch(`/threat-intel/indicators/search?${queryString}`
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.detail ||
              "Unable to load indicators."
          );
        }

        setItems(
          Array.isArray(data.items)
            ? data.items
            : []
        );

        setPagination((current) => ({
          ...current,
          ...(data.pagination || {}),
        }));
      } catch (loadError) {
        console.error(
          "IOC Explorer load error:",
          loadError
        );

        setError(
          loadError.message ||
            "Unable to load indicators."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [queryString]
  );

  const loadFeeds = useCallback(async () => {
    try {
      const response = await apiFetch(`/threat-intel/feeds`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to load feeds."
        );
      }

      setFeeds(
        Array.isArray(data)
          ? data
          : data.feeds || []
      );
    } catch (feedError) {
      console.error(
        "Threat feed option error:",
        feedError
      );
    }
  }, []);

  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  useEffect(() => {
    loadIndicators();
  }, [loadIndicators]);

  useEffect(() => {
    const timer = window.setInterval(
      () => loadIndicators(true),
      30000
    );

    return () =>
      window.clearInterval(timer);
  }, [loadIndicators]);

  const openIndicator = async (
    indicatorId
  ) => {
    setDetailsLoading(true);
    setError("");

    try {
      const response = await apiFetch(`/threat-intel/indicators/${indicatorId}`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to load indicator details."
        );
      }

      setSelectedIndicator(data);
      setRelationshipGraph(null);
      setIndicatorTimeline(null);
      setIndicatorHunts([]);
      setHuntMessage("");
    } catch (detailsError) {
      console.error(
        "Indicator details error:",
        detailsError
      );

      setError(
        detailsError.message ||
          "Unable to load indicator details."
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  const loadRelationshipGraph = async (
    indicatorId
  ) => {
    setGraphLoading(true);
    setError("");

    try {
      const response = await apiFetch(`/threat-intel/indicators/${indicatorId}/relationships`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to load IOC relationship graph."
        );
      }

      setRelationshipGraph(data);
    } catch (graphError) {
      console.error(
        "IOC relationship graph error:",
        graphError
      );

      setError(
        graphError.message ||
          "Unable to load IOC relationship graph."
      );
    } finally {
      setGraphLoading(false);
    }
  };

  const loadIndicatorTimeline = async (
    indicatorId
  ) => {
    setTimelineLoading(true);
    setError("");

    try {
      const response = await apiFetch(`/threat-intel/indicators/${indicatorId}/timeline`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to load IOC timeline."
        );
      }

      setIndicatorTimeline(data);
    } catch (timelineError) {
      console.error(
        "IOC timeline error:",
        timelineError
      );

      setError(
        timelineError.message ||
          "Unable to load IOC timeline."
      );
    } finally {
      setTimelineLoading(false);
    }
  };

  const loadIndicatorHunts = async (
    indicatorId
  ) => {
    setHuntsLoading(true);
    setError("");

    try {
      const response = await apiFetch(`/threat-intel/indicators/${indicatorId}/hunts`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to load related threat hunts."
        );
      }

      setIndicatorHunts(
        data.hunts || []
      );
    } catch (huntError) {
      console.error(
        "IOC related hunts error:",
        huntError
      );

      setError(
        huntError.message ||
          "Unable to load related threat hunts."
      );
    } finally {
      setHuntsLoading(false);
    }
  };

  const launchIndicatorHunt = async (
    indicatorId
  ) => {
    setHuntLaunching(true);
    setHuntMessage("");
    setError("");

    try {
      const response = await apiFetch(`/threat-intel/indicators/${indicatorId}/launch-hunt`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            created_by:
              "George Taylor",
            assigned_analyst:
              "George Taylor",
            assigned_team:
              "Wireless Press SOC",
            actor_role:
              "SOC Manager",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : data.detail?.message ||
              "Unable to launch threat hunt."
        );
      }

      setHuntMessage(
        `Threat hunt ${
          data.hunt?.hunt_number ||
          data.hunt?.id ||
          ""
        } created successfully.`
      );

      await loadIndicatorHunts(
        indicatorId
      );

      await loadIndicatorTimeline(
        indicatorId
      );
    } catch (huntError) {
      console.error(
        "IOC threat hunt launch error:",
        huntError
      );

      setError(
        huntError.message ||
          "Unable to launch threat hunt."
      );
    } finally {
      setHuntLaunching(false);
    }
  };

  const updateFilter = (
    name,
    value
  ) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));

    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const submitSearch = (event) => {
    event.preventDefault();

    updateFilter(
      "search",
      draftSearch.trim()
    );
  };

  const clearFilters = () => {
    setDraftSearch("");

    setFilters({
      search: "",
      indicator_type: "",
      severity: "",
      feed_id: "",
      threat_category: "",
      threat_actor: "",
      campaign: "",
      malware_family: "",
      tlp: "",
      active: "",
      expired: "",
      minimum_confidence: 0,
      maximum_confidence: 100,
      minimum_reputation: -1000,
      maximum_reputation: 1000,
      sort: "updated_at",
      order: "desc",
    });

    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const changePage = (page) => {
    setPagination((current) => ({
      ...current,
      page,
    }));
  };

  const exportJson = () => {
    const content = JSON.stringify(
      items,
      null,
      2
    );

    const blob = new Blob(
      [content],
      {
        type: "application/json",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = url;
    link.download =
      "wirelesspress-threat-indicators.json";

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="iocExplorerPage">
      <header className="iocExplorerHeader">
        <div>
          <p className="iocExplorerEyebrow">
            WirelessPress Threat Intelligence
          </p>

          <h1>IOC Explorer</h1>

          <p>
            Search, filter, sort, and investigate
            threat indicators across connected
            intelligence feeds.
          </p>
        </div>

        <div className="iocExplorerHeaderActions">
          <Link
            to="/threat-intelligence"
            className="iocExplorerSecondaryButton"
          >
            Dashboard
          </Link>

          <button
            type="button"
            className="iocExplorerSecondaryButton"
            onClick={exportJson}
            disabled={items.length === 0}
          >
            Export JSON
          </button>

          <button
            type="button"
            className="iocExplorerPrimaryButton"
            onClick={() =>
              loadIndicators(true)
            }
            disabled={refreshing}
          >
            {refreshing
              ? "Refreshing..."
              : "Refresh"}
          </button>
        </div>
      </header>

      {error ? (
        <div className="iocExplorerError">
          {error}
        </div>
      ) : null}

      <section className="iocExplorerSearchPanel">
        <form
          className="iocExplorerSearchForm"
          onSubmit={submitSearch}
        >
          <input
            type="search"
            value={draftSearch}
            onChange={(event) =>
              setDraftSearch(
                event.target.value
              )
            }
            placeholder="Search IP, domain, URL, hash, email, threat actor, campaign, malware family..."
          />

          <button
            type="submit"
            className="iocExplorerPrimaryButton"
          >
            Search
          </button>

          <button
            type="button"
            className="iocExplorerSecondaryButton"
            onClick={clearFilters}
          >
            Clear
          </button>
        </form>

        <div className="iocExplorerFilters">
          <label>
            IOC Type
            <select
              value={
                filters.indicator_type
              }
              onChange={(event) =>
                updateFilter(
                  "indicator_type",
                  event.target.value
                )
              }
            >
              <option value="">
                All types
              </option>
              <option value="ipv4">
                IPv4
              </option>
              <option value="ipv6">
                IPv6
              </option>
              <option value="domain">
                Domain
              </option>
              <option value="url">
                URL
              </option>
              <option value="email">
                Email
              </option>
              <option value="md5">
                MD5
              </option>
              <option value="sha1">
                SHA1
              </option>
              <option value="sha256">
                SHA256
              </option>
              <option value="filename">
                Filename
              </option>
              <option value="cve">
                CVE
              </option>
            </select>
          </label>

          <label>
            Severity
            <select
              value={filters.severity}
              onChange={(event) =>
                updateFilter(
                  "severity",
                  event.target.value
                )
              }
            >
              <option value="">
                All severities
              </option>
              <option value="critical">
                Critical
              </option>
              <option value="high">
                High
              </option>
              <option value="medium">
                Medium
              </option>
              <option value="low">
                Low
              </option>
              <option value="informational">
                Informational
              </option>
            </select>
          </label>

          <label>
            Feed
            <select
              value={filters.feed_id}
              onChange={(event) =>
                updateFilter(
                  "feed_id",
                  event.target.value
                )
              }
            >
              <option value="">
                All feeds
              </option>

              {feeds.map((feed) => (
                <option
                  key={feed.id}
                  value={feed.id}
                >
                  {feed.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Status
            <select
              value={filters.active}
              onChange={(event) =>
                updateFilter(
                  "active",
                  event.target.value
                )
              }
            >
              <option value="">
                All statuses
              </option>
              <option value="true">
                Active
              </option>
              <option value="false">
                Inactive
              </option>
            </select>
          </label>

          <label>
            Expiration
            <select
              value={filters.expired}
              onChange={(event) =>
                updateFilter(
                  "expired",
                  event.target.value
                )
              }
            >
              <option value="">
                All indicators
              </option>
              <option value="false">
                Current
              </option>
              <option value="true">
                Expired
              </option>
            </select>
          </label>

          <label>
            TLP
            <select
              value={filters.tlp}
              onChange={(event) =>
                updateFilter(
                  "tlp",
                  event.target.value
                )
              }
            >
              <option value="">
                All TLP levels
              </option>
              <option value="TLP:CLEAR">
                TLP:CLEAR
              </option>
              <option value="TLP:GREEN">
                TLP:GREEN
              </option>
              <option value="TLP:AMBER">
                TLP:AMBER
              </option>
              <option value="TLP:AMBER+STRICT">
                TLP:AMBER+STRICT
              </option>
              <option value="TLP:RED">
                TLP:RED
              </option>
            </select>
          </label>

          <label>
            Sort
            <select
              value={filters.sort}
              onChange={(event) =>
                updateFilter(
                  "sort",
                  event.target.value
                )
              }
            >
              <option value="updated_at">
                Updated
              </option>
              <option value="created_at">
                Created
              </option>
              <option value="last_seen">
                Last Seen
              </option>
              <option value="first_seen">
                First Seen
              </option>
              <option value="confidence">
                Confidence
              </option>
              <option value="reputation_score">
                Reputation
              </option>
              <option value="severity">
                Severity
              </option>
              <option value="indicator_value">
                Indicator
              </option>
            </select>
          </label>

          <label>
            Order
            <select
              value={filters.order}
              onChange={(event) =>
                updateFilter(
                  "order",
                  event.target.value
                )
              }
            >
              <option value="desc">
                Descending
              </option>
              <option value="asc">
                Ascending
              </option>
            </select>
          </label>

          <label>
            Page Size
            <select
              value={pagination.page_size}
              onChange={(event) =>
                setPagination(
                  (current) => ({
                    ...current,
                    page: 1,
                    page_size: Number(
                      event.target.value
                    ),
                  })
                )
              }
            >
              <option value="25">
                25
              </option>
              <option value="50">
                50
              </option>
              <option value="100">
                100
              </option>
              <option value="200">
                200
              </option>
            </select>
          </label>
        </div>
      </section>

      <section className="iocExplorerPanel">
        <div className="iocExplorerPanelHeader">
          <div>
            <span>
              THREAT INDICATOR INVENTORY
            </span>

            <h2>
              Indicators
            </h2>
          </div>

          <div className="iocExplorerCount">
            {formatNumber(
              pagination.total
            )}{" "}
            total
          </div>
        </div>

        <div className="iocExplorerTableWrapper">
          <table className="iocExplorerTable">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Type</th>
                <th>Severity</th>
                <th>Confidence</th>
                <th>Reputation</th>
                <th>Threat Context</th>
                <th>Feed</th>
                <th>Matches</th>
                <th>Last Seen</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="11"
                    className="iocExplorerEmpty"
                  >
                    Loading indicators...
                  </td>
                </tr>
              ) : null}

              {!loading &&
              items.length === 0 ? (
                <tr>
                  <td
                    colSpan="11"
                    className="iocExplorerEmpty"
                  >
                    No indicators match the
                    current filters.
                  </td>
                </tr>
              ) : null}

              {!loading &&
                items.map((indicator) => (
                  <tr key={indicator.id}>
                    <td>
                      <strong className="iocExplorerIndicatorValue">
                        {
                          indicator.indicator_value
                        }
                      </strong>

                      <small>
                        {indicator.title ||
                          "Untitled indicator"}
                      </small>
                    </td>

                    <td>
                      {formatLabel(
                        indicator.indicator_type
                      )}
                    </td>

                    <td>
                      <span
                        className={`iocExplorerSeverity severity-${String(
                          indicator.severity
                        ).toLowerCase()}`}
                      >
                        {formatLabel(
                          indicator.severity
                        )}
                      </span>
                    </td>

                    <td>
                      {indicator.confidence}%
                    </td>

                    <td>
                      {indicator.reputation_score ??
                        "—"}
                    </td>

                    <td>
                      {indicator.threat_actor ||
                        indicator.campaign ||
                        indicator.malware_family ||
                        indicator.threat_category ||
                        "Unclassified"}
                    </td>

                    <td>
                      {indicator.feed_name ||
                        "Internal"}
                    </td>

                    <td>
                      {formatNumber(
                        indicator.match_count
                      )}
                    </td>

                    <td>
                      {formatDate(
                        indicator.last_seen
                      )}
                    </td>

                    <td>
                      <span
                        className={
                          indicator.expired
                            ? "iocExplorerStatus expired"
                            : indicator.active
                              ? "iocExplorerStatus active"
                              : "iocExplorerStatus inactive"
                        }
                      >
                        {indicator.expired
                          ? "Expired"
                          : indicator.active
                            ? "Active"
                            : "Inactive"}
                      </span>
                    </td>

                    <td>
                      <button
                        type="button"
                        className="iocExplorerViewButton"
                        onClick={() =>
                          openIndicator(
                            indicator.id
                          )
                        }
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="iocExplorerPagination">
          <button
            type="button"
            disabled={
              !pagination.has_previous
            }
            onClick={() =>
              changePage(
                pagination.page - 1
              )
            }
          >
            Previous
          </button>

          <span>
            Page {pagination.page} of{" "}
            {pagination.total_pages || 1}
          </span>

          <button
            type="button"
            disabled={
              !pagination.has_next
            }
            onClick={() =>
              changePage(
                pagination.page + 1
              )
            }
          >
            Next
          </button>
        </div>
      </section>

      {selectedIndicator ? (
        <div
          className="iocExplorerDrawerOverlay"
          onClick={() =>
            setSelectedIndicator(null)
          }
        >
          <aside
            className="iocExplorerDrawer"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="iocExplorerDrawerHeader">
              <div>
                <span>
                  INDICATOR INVESTIGATION
                </span>

                <h2>
                  {selectedIndicator.indicator_value}
                </h2>
              </div>

              <button
                type="button"
                onClick={() => {
                  setSelectedIndicator(null);
                  setRelationshipGraph(null);
                  setIndicatorTimeline(null);
                  setIndicatorHunts([]);
                  setHuntMessage("");
                }}
              >
                ×
              </button>
            </div>

            <div className="iocExplorerDrawerActions">
              <button
                type="button"
                className="iocExplorerPrimaryButton"
                disabled={graphLoading}
                onClick={() =>
                  loadRelationshipGraph(
                    selectedIndicator.id
                  )
                }
              >
                {graphLoading
                  ? "Loading Graph..."
                  : relationshipGraph
                    ? "Refresh Relationship Graph"
                    : "View Relationship Graph"}
              </button>

              <button
                type="button"
                className="iocExplorerSecondaryButton"
                disabled={timelineLoading}
                onClick={() =>
                  loadIndicatorTimeline(
                    selectedIndicator.id
                  )
                }
              >
                {timelineLoading
                  ? "Loading Timeline..."
                  : indicatorTimeline
                    ? "Refresh Timeline"
                    : "View Timeline"}
              </button>

              <button
                type="button"
                className="iocExplorerPrimaryButton"
                disabled={huntLaunching}
                onClick={() =>
                  launchIndicatorHunt(
                    selectedIndicator.id
                  )
                }
              >
                {huntLaunching
                  ? "Launching Hunt..."
                  : "Launch Threat Hunt"}
              </button>

              <button
                type="button"
                className="iocExplorerSecondaryButton"
                disabled={huntsLoading}
                onClick={() =>
                  loadIndicatorHunts(
                    selectedIndicator.id
                  )
                }
              >
                {huntsLoading
                  ? "Loading Hunts..."
                  : indicatorHunts.length > 0
                    ? "Refresh Related Hunts"
                    : "View Related Hunts"}
              </button>
            </div>

            {detailsLoading ? (
              <p>Loading details...</p>
            ) : (
              <>
                <div className="iocExplorerDrawerBadges">
                  <span
                    className={`iocExplorerSeverity severity-${String(
                      selectedIndicator.severity
                    ).toLowerCase()}`}
                  >
                    {formatLabel(
                      selectedIndicator.severity
                    )}
                  </span>

                  <span>
                    {formatLabel(
                      selectedIndicator.indicator_type
                    )}
                  </span>

                  <span>
                    {selectedIndicator.tlp ||
                      "TLP:CLEAR"}
                  </span>
                </div>

                <section className="iocExplorerDrawerSection">
                  <h3>Summary</h3>

                  <p>
                    {selectedIndicator.description ||
                      "No description has been provided."}
                  </p>
                </section>

                <section className="iocExplorerDrawerSection">
                  <h3>
                    Threat Context
                  </h3>

                  <dl>
                    <div>
                      <dt>Confidence</dt>
                      <dd>
                        {
                          selectedIndicator.confidence
                        }
                        %
                      </dd>
                    </div>

                    <div>
                      <dt>Reputation</dt>
                      <dd>
                        {selectedIndicator.reputation_score ??
                          "Not scored"}
                      </dd>
                    </div>

                    <div>
                      <dt>
                        Threat Category
                      </dt>
                      <dd>
                        {selectedIndicator.threat_category ||
                          "Unclassified"}
                      </dd>
                    </div>

                    <div>
                      <dt>
                        Malware Family
                      </dt>
                      <dd>
                        {selectedIndicator.malware_family ||
                          "Not identified"}
                      </dd>
                    </div>

                    <div>
                      <dt>Threat Actor</dt>
                      <dd>
                        {selectedIndicator.threat_actor ||
                          "Not identified"}
                      </dd>
                    </div>

                    <div>
                      <dt>Campaign</dt>
                      <dd>
                        {selectedIndicator.campaign ||
                          "Not identified"}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className="iocExplorerDrawerSection">
                  <h3>Lifecycle</h3>

                  <dl>
                    <div>
                      <dt>First Seen</dt>
                      <dd>
                        {formatDate(
                          selectedIndicator.first_seen
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt>Last Seen</dt>
                      <dd>
                        {formatDate(
                          selectedIndicator.last_seen
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt>Valid From</dt>
                      <dd>
                        {formatDate(
                          selectedIndicator.valid_from
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt>Valid Until</dt>
                      <dd>
                        {formatDate(
                          selectedIndicator.valid_until
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt>Created</dt>
                      <dd>
                        {formatDate(
                          selectedIndicator.created_at
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt>Updated</dt>
                      <dd>
                        {formatDate(
                          selectedIndicator.updated_at
                        )}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className="iocExplorerDrawerSection">
                  <h3>Tags</h3>

                  <div className="iocExplorerTags">
                    {Array.isArray(
                      selectedIndicator.tags
                    ) &&
                    selectedIndicator.tags
                      .length > 0 ? (
                      selectedIndicator.tags.map(
                        (tag) => (
                          <span key={tag}>
                            {tag}
                          </span>
                        )
                      )
                    ) : (
                      <span>
                        No tags
                      </span>
                    )}
                  </div>
                </section>

                {huntMessage ? (
                  <div className="iocHuntSuccess">
                    {huntMessage}
                  </div>
                ) : null}

                {indicatorHunts.length > 0 ? (
                  <section className="iocExplorerDrawerSection">
                    <h3>
                      Related Threat Hunts
                    </h3>

                    <div className="iocHuntList">
                      {indicatorHunts.map(
                        (hunt) => (
                          <article
                            key={
                              hunt.relationship_id ||
                              hunt.id
                            }
                          >
                            <div className="iocHuntHeader">
                              <div>
                                <strong>
                                  {
                                    hunt.hunt_number
                                  }
                                </strong>

                                <span>
                                  {
                                    hunt.hunt_name
                                  }
                                </span>
                              </div>

                              <span
                                className={`iocHuntStatus status-${String(
                                  hunt.status ||
                                    "unknown"
                                ).toLowerCase()}`}
                              >
                                {formatLabel(
                                  hunt.status
                                )}
                              </span>
                            </div>

                            <p>
                              {hunt.description ||
                                hunt.hypothesis}
                            </p>

                            <dl>
                              <div>
                                <dt>
                                  Priority
                                </dt>
                                <dd>
                                  {hunt.priority}
                                </dd>
                              </div>

                              <div>
                                <dt>
                                  Severity
                                </dt>
                                <dd>
                                  {formatLabel(
                                    hunt.severity
                                  )}
                                </dd>
                              </div>

                              <div>
                                <dt>
                                  Analyst
                                </dt>
                                <dd>
                                  {hunt.assigned_analyst ||
                                    "Unassigned"}
                                </dd>
                              </div>

                              <div>
                                <dt>
                                  Created
                                </dt>
                                <dd>
                                  {formatDate(
                                    hunt.created_at
                                  )}
                                </dd>
                              </div>

                              <div>
                                <dt>
                                  Incident
                                </dt>
                                <dd>
                                  {hunt.incident_id ||
                                    "Not linked"}
                                </dd>
                              </div>

                              <div>
                                <dt>
                                  Match
                                </dt>
                                <dd>
                                  {hunt.match_id ||
                                    "Not linked"}
                                </dd>
                              </div>
                            </dl>
                          </article>
                        )
                      )}
                    </div>
                  </section>
                ) : null}

                {indicatorTimeline ? (
                  <section className="iocExplorerDrawerSection">
                    <h3>IOC Timeline</h3>

                    <div className="iocTimelineSummary">
                      <article>
                        <span>Events</span>
                        <strong>
                          {
                            indicatorTimeline.statistics
                              ?.event_count || 0
                          }
                        </strong>
                      </article>

                      <article>
                        <span>Matches</span>
                        <strong>
                          {
                            indicatorTimeline.statistics
                              ?.match_count || 0
                          }
                        </strong>
                      </article>

                      <article>
                        <span>Incidents</span>
                        <strong>
                          {
                            indicatorTimeline.statistics
                              ?.incident_count || 0
                          }
                        </strong>
                      </article>
                    </div>

                    <div className="iocTimelineList">
                      {(indicatorTimeline.timeline || []).map(
                        (item, index) => (
                          <article
                            key={`${item.event_type}-${item.timestamp}-${index}`}
                            className={`iocTimelineItem severity-${String(
                              item.severity || "informational"
                            ).toLowerCase()}`}
                          >
                            <div className="iocTimelineMarker" />

                            <div className="iocTimelineContent">
                              <div className="iocTimelineHeader">
                                <strong>
                                  {item.title}
                                </strong>

                                <span>
                                  {formatDate(
                                    item.timestamp
                                  )}
                                </span>
                              </div>

                              <div className="iocTimelineMeta">
                                <span>
                                  {formatLabel(
                                    item.event_type
                                  )}
                                </span>

                                <span>
                                  {formatLabel(
                                    item.source
                                  )}
                                </span>

                                {item.severity ? (
                                  <span>
                                    {formatLabel(
                                      item.severity
                                    )}
                                  </span>
                                ) : null}
                              </div>

                              {item.description ? (
                                <p>
                                  {item.description}
                                </p>
                              ) : null}
                            </div>
                          </article>
                        )
                      )}
                    </div>
                  </section>
                ) : null}

                {relationshipGraph ? (
                  <section className="iocExplorerDrawerSection">
                    <h3>
                      Relationship Graph
                    </h3>

                    <div className="iocGraphSummary">
                      <article>
                        <span>Nodes</span>
                        <strong>
                          {
                            relationshipGraph.statistics
                              ?.node_count || 0
                          }
                        </strong>
                      </article>

                      <article>
                        <span>Edges</span>
                        <strong>
                          {
                            relationshipGraph.statistics
                              ?.edge_count || 0
                          }
                        </strong>
                      </article>

                      <article>
                        <span>Matches</span>
                        <strong>
                          {
                            relationshipGraph.statistics
                              ?.match_count || 0
                          }
                        </strong>
                      </article>
                    </div>

                    <div className="iocGraphNodes">
                      {(relationshipGraph.graph?.nodes || []).map(
                        (node) => (
                          <article
                            key={node.id}
                            className={`iocGraphNode node-${node.type}`}
                          >
                            <span>
                              {formatLabel(
                                node.type
                              )}
                            </span>

                            <strong>
                              {node.label}
                            </strong>

                            {node.data?.status ? (
                              <small>
                                Status:{" "}
                                {
                                  node.data.status
                                }
                              </small>
                            ) : null}
                          </article>
                        )
                      )}
                    </div>

                    <div className="iocGraphEdges">
                      <h4>
                        Relationships
                      </h4>

                      {(relationshipGraph.graph?.edges || []).length ===
                      0 ? (
                        <p>
                          No relationships found.
                        </p>
                      ) : (
                        (relationshipGraph.graph?.edges || []).map(
                          (edge) => (
                            <article key={edge.id}>
                              <code>
                                {edge.source}
                              </code>

                              <span>
                                {formatLabel(
                                  edge.relationship
                                )}
                              </span>

                              <code>
                                {edge.target}
                              </code>
                            </article>
                          )
                        )
                      )}
                    </div>
                  </section>
                ) : null}
              </>
            )}
          </aside>
        </div>
      ) : null}
    </div>
  );
}

export default ThreatIndicatorsPage;
